// Packages
import DOMPurify from 'dompurify'
import moment from 'moment'
import validator from 'validator'
// Helpers
import {
  convertBigToPennies,
} from '../../../shared/calculations'

import AllFlags from '../shared/AllFlags.json'
import _Store from '../store/store'
import algoliaSearch from './algoliaSearch'
import {
  Debtor,
  Invoice,
  InvoiceUpdate,
  Reserve,
  ReserveAccount,
  Tenant,
} from './api'
import {
  ClientApiDelegator,
  InvoiceApiDelegator,
  ReserveApiDelegator,
  TransfersApiDelegator,
} from './api-delegate'
import { BrokerPaymentApiDelegator } from './api-delegate/broker-payments-api-delegator'
import {
  DashboardV2,
  InvoiceV2,
  ReserveV2,
  MaintenanceV2,
  ProcessingNotesV2,
  BrokerV2,
} from './api-v2'
import {
  searchV2Invoices,
  searchV2Reserves,
  searchV2ReserveAccount,
  transformV2RawInvoice,
} from './api-v2/helpers'
import {
  CError,
  CLog,
} from './consoleMixin'
import {
  LDFlags,
} from './constants'
import {
  XSSProtection,
  transformRawInvoice,
} from './helpers'
import { LaunchDarkly } from './launchDarkly'
import {
  formatUniversalSearchIDProperty,
  searchInvoices,
} from './searchHelpers'
import {
  captureSentryEvent,
} from './sentryMixin'
import { MaintenanceModeMapper, ReservesMapper, ProcessingNotesMapper, DebtorMapper } from './api-v2/mappings'
import { ReserveAccountApiDelegator } from './api-delegate/reserve-account-api-delegator'

/**
 * @typedef {import('./api-v2/js-doc').UiInvoiceModel} UiInvoiceModel
 * @typedef {import('./api-v2/js-doc').UiInvoicePossibleDuplicate} UiInvoicePossibleDuplicate
 * @typedef {import('./api-v2/js-doc').UiReserveRateHistoryResponse} UiReserveRateHistoryResponse
 * @typedef {import('./api-v2/js-doc').UiClientLimitHistoryResponse} UiClientLimitHistoryResponse
 * @typedef {import('./api-v2/js-doc').UiDebtorLimitHistoryResponse} UiDebtorLimitHistoryResponse
 * @typedef {import('./api-v2/js-doc').V1CreateInvoiceRequest} V1CreateInvoiceRequest
 * @typedef {import('./api-v2/js-doc').V1PurchaseInvoiceRequest} V1PurchaseInvoiceRequest
 * @typedef {import('./api-v2/js-doc').V1RejectInvoiceRequest} V1RejectInvoiceRequest
 * @typedef {import('./api-v2/js-doc').V1VerifyInvoiceRequest} V1VerifyInvoiceRequest
 * @typedef {import('./api-v2/js-doc').V1WaitOnVerificationInvoiceRequest} V1WaitOnVerificationInvoiceRequest
 * @typedef {import('./api-v2/js-doc').V1BypassVerificationInvoiceRequest} V1BypassInvoiceRequest
 * @typedef {import('./api-v2/js-doc').V1RevertInvoiceRequest} V1RevertInvoiceRequest
 * @typedef {import('./api-v2/js-doc').V1InvoiceResponse} V1InvoiceResponse
 * @typedef {import('./api-v2/js-doc').V2VerifyInvoiceRequest} V2VerifyInvoiceRequest
 * @typedef {import('./api-v2/js-doc').V2InvoiceResponse} V2InvoiceResponse
 * @typedef {import('./api-v2/js-doc').V2CreateInvoiceDocumentRequest} V2CreateInvoiceDocumentRequest
 * @typedef {import('./api-v2/js-doc').V1UpdateClientRequest} V1UpdateClientRequest
 * @typedef {import('./api-v2/js-doc').V2ClientFactoringConfigResponse} V2ClientFactoringConfigResponse
 * @typedef {import('./api-v2/js-doc').V1CreateBrokerPaymentRequest} V1CreateBrokerPaymentRequest
 * @typedef {import('./api-v2/js-doc').V1BrokerPaymentResponse} V1BrokerPaymentResponse
 * @typedef {import('./api-v2/js-doc').V1UpdateBrokerPaymentRequest} V1UpdateBrokerPaymentRequest
 * @typedef {import('./api-v2/js-doc').V1UpdateInvoiceRequest} V1UpdateInvoiceRequest
 * @typedef {import('./api-v2/js-doc').V1ReserveRateHistoryResponse} V1ReserveRateHistoryResponse
 * @typedef {import('./api-v2/js-doc').V1ReserveRateHistoryResponse} V1ReserveResponse
 * @typedef {import('./api-v2/js-doc').V1CreateReserveAccountRequest} V1CreateReserveAccountRequest
 * @typedef {import('./api-v2/js-doc').V2MaintenanceMode} V2MaintenanceMode
 * @typedef {import('./api-v2/js-doc').V2MaintenanceModeUpdateRequest} V2MaintenanceModeUpdateRequest
 * @typedef {import('./api-v2/js-doc').V1CreateProcessingNotesRequest} V1CreateProcessingNotesRequest
 * @typedef {import('./api-v2/js-doc').V1UpdateProcessingNotesRequest} V1UpdateProcessingNotesRequest
 * @typedef {import('./api-v2/js-doc').V2UpdateBrokerFactoringConfigRequest} V2UpdateBrokerFactoringConfigRequest
 * @typedef {import('./api-v2/js-doc').V1UpdateDebtorRequest} V1UpdateDebtorRequest
 */

class FactoringAPI {

  /**
   * Checks to see if the client has the 'ingest-through-v2' flag enabled
   * @param {null|string} clientId - ID of the client
   * @returns {Promise<boolean>}
   */
  async #isIngestThroughV2EnabledForClient (clientId) {
    return await LaunchDarkly.isFlagEnabledForClient(clientId, LDFlags.ingestThroughV2)
  }

  /**
   * @param {string} clientId - client ID
   * @returns {Promise<boolean>}
   */
  async #shouldSendToV2 (clientId = null) {
    const featureFlag = LDFlags.useV2
    const value = await LaunchDarkly.isFlagEnabledForClient(clientId, featureFlag)

    CLog('Feature flag', featureFlag, 'has value of', value, 'for client id', clientId)

    if (!value) {
      CLog(`Feature flag ${featureFlag} is disabled for`, LaunchDarkly.context())
      return false
    }

    return value
  }

  // This function allows us to make V2 calls (silently) alongside V1 calls without disrupting the user
  // to any issues with the V2 call; however, if listing is enabled or a function passes false for
  // failSilently, the error will be bubbled up. Without this function, we'd have to wrap every V2
  // function call with a check if the client is enabled for v2 and a check for if we should respond
  // to the error

  /**
   * @param {any} fn - Function to be executed
   * @param {string} clientId - client ID
   * @param {{failSilently?: boolean, operationName: string}} options - Configuration options
   * @returns {Promise<void>}
   */
  async #v2FnWrapper (fn, clientId = null, options) {
    options.failSilently ??= true
    try {
      if (await this.#shouldSendToV2(clientId)) {
        await fn()
      }
    }
    catch (error) {
      CError(error)
      // TODO: add a tag to this event for reporting
      try {
        captureSentryEvent(
          `V2 REQUEST: ${options.operationName}`,
          {
            clientID: clientId,
            url: error.config?.url,
            request: error.request?.body,
            response: error.response?.config?.data,
            details: error,
          }
        )
      }
      catch (error) {
        CError('Error on captureSentryEvent FactoringAPI', error)
      }

      if (!options.failSilently || LaunchDarkly.isFlagEnabled(LDFlags.useV2InvoiceListing)) {
        throw error
      }
    }
  }

  async bypassVerification (payload) {
    /** * @type {V1BypassInvoiceRequest} */
    const v1Request = {
      id: payload.id,
      requires_verification: false,
      skipped_verification: true, // Used to create a unique invoice update
    }
    return await new InvoiceApiDelegator().bypassVerification(payload.client_id, v1Request)
  }

  /**
   * @param {string} id - ID of the broker payment
   * @param {string} clientId - ID of the client
   * @returns {Promise<V1BrokerPaymentResponse>}
   */
  async deleteDebtorPayment (id, clientId) {
    return new BrokerPaymentApiDelegator().deleteBrokerPayment(clientId, id)
  }

  async deleteInvoice (invoice) {
    return await new InvoiceApiDelegator().deleteInvoice(invoice.client_id, invoice.id)
  }

  async deleteInvoiceUpdates (componentData) {
    if (await this.#isIngestThroughV2EnabledForClient(componentData.invoice.client.id)) {
      return await this.#deleteInvoiceUpdatesV2(
        componentData.invoice.id,
        componentData.update.id,
        true
      ) // TODO: update when mappers are done
    }
    else {
      await this.#deleteInvoiceUpdatesV1(componentData.update.id)
      await this.#v2FnWrapper(
        () => {
          return this.#deleteInvoiceUpdatesV2(
            componentData.invoice.id,
            componentData.update.id
          )
        },
        // invoice comes from props
        componentData.invoice.client.id,
        {
          operationName: 'delete-invoice-updates',
        }
      )
    }
  }

  async #deleteInvoiceUpdatesV1 (id) {
    return await InvoiceUpdate.delete(id)
  }

  async #deleteInvoiceUpdatesV2 (invoiceId, activityId, __ingestThrough = false) {
    const v2Payload = {
      __ingestThrough,
    }
    await new InvoiceV2().deleteTag(invoiceId, activityId, v2Payload)
  }

  async getChargebackInvoices (id, page) {
    const qualifiedChargebackDate = moment().subtract(60, 'days').toDate()
    CLog('qualified chargeback date:', qualifiedChargebackDate)

    if (LaunchDarkly.isFlagEnabled(LDFlags.useV2InvoiceListing)) {
      CLog('Fetching chargeback invoices from V2')
      return await searchV2Invoices('approved', {
        inputs: {
          clientID: id,
          purchasedDateLt: qualifiedChargebackDate,
        },
      },
      {
        page,
      })
    }
    else {
      return (await Invoice.queryList({
        client_id: id,
        limit: 25,
        lt___approved_date: qualifiedChargebackDate,
        offset: (page - 1) * 25,
        sort_by: 'approved_date',
        sort_direction: 'asc',
        status: 'approved',
      })).data
    }
  }

  async getClients (search) {
    return (await algoliaSearch.clientSearch(search)).data.hits
  }

  async getDashboardNotificationStats (id) {
    if (LaunchDarkly.isFlagEnabled(LDFlags.useV2InvoiceListing)) {
      const response = (await new DashboardV2().getNotificationStats(id)).data

      return {
        chargebacksAmount: response.upcomingChargebacksStats.total,
        chargebacksCount: response.upcomingChargebacksStats.count,
        originalsAmount: response.originalsRequiredStats.total,
        originalsCount: response.originalsRequiredStats.count,
        paperworkAmount: response.paperworkIssuesStats.total,
        paperworkCount: response.paperworkIssuesStats.count,
      }
    }
    else {
      const getChargebackStats = (await Invoice.queryList({
        lt___approved_date: moment().subtract(60, 'days').toDate(),
        client_id: id,
        limit: 1,
        offset: 0,
        status: 'approved',
      })).data
      const getOriginalStats = (await Invoice.queryList({
        client_id: id,
        originals_required: true,
        status: 'approved',
      })).data
      const getPaperworkStats = (await Invoice.queryList({
        client_id: id,
        paperwork_issues: true,
        status: 'approved',
      })).data

      return {
        chargebacksAmount: getChargebackStats?.facets_stats.total_amount.sum || 0,
        chargebacksCount: getChargebackStats.count,
        originalsAmount: getOriginalStats?.facets_stats.total_amount.sum || 0,
        originalsCount: getOriginalStats.count,
        paperworkAmount: getPaperworkStats?.facets_stats.total_amount.sum || 0,
        paperworkCount: getPaperworkStats.count,
      }
    }
  }

  async getDebtors (input, statuses, returnDebtorNotFound, page) {
    const filters = `status:${statuses.join(' OR status:')}`
    const query = `${DOMPurify.sanitize(input)}`
    const results = (await Debtor.search({
      filters,
      includeNotFoundDebtor: returnDebtorNotFound
        ? true
        : null,
      page,
      query,
    }, 5)).data

    // [BANDAID](unsure of cause) addresses error where a null value is returned in results
    results.hits = results.hits.filter((hit) => {
      if (!hit) {
        // log this in sentry for better debugging
        captureSentryEvent('NULL in Debtor Search', {
          filters,
          query,
        }, 'debug')
      }
      return hit
    })

    return results
  }

  /**
   * @param {string} invoiceId - ID of the invoice
   * @returns {Promise<V1BrokerPaymentResponse[]>}
   */
  async getDebtorPayments (invoiceId) {
    return new BrokerPaymentApiDelegator().getBrokerPayments(invoiceId)
  }

  async getInvoice (id) {
    if (LaunchDarkly.isFlagEnabled(LDFlags.useV2InvoiceListing)) {
      // TODO: remove transform once we move from V1 to V2 for listing (FE will be updated to V2 mappings)
      return transformV2RawInvoice((await new InvoiceV2().get(id)).data)
    }
    return (await Invoice.get(id)).data
  }

  async getInvoices ($data, status = null, $props = null, specialParams = null) {
    if (LaunchDarkly.isFlagEnabled(LDFlags.useV2InvoiceListing)) {
      CLog('Fetching invoice from V2')
      return await searchV2Invoices(status, $data, $props)
    }
    else {
      // Non-universal search
      if (status) {
        return await searchInvoices(status, $data, $props, specialParams)
      }
      // Universal search
      else {
        return (await Invoice.queryList({
          ...formatUniversalSearchIDProperty($data.ids),
          include: 'all',
          like___load_number: $data.loadNumber.value,
          offset: ($data.invoicePage - 1) * $data.requestLimit,
        }, $data.requestLimit)).data
      }
    }
  }

  async getInvoiceIDsForInvoiceDetails (searchParams) {
    if (LaunchDarkly.isFlagEnabled(LDFlags.useV2InvoiceListing)) {
      // TODO: need a way to pass a request limit of 1
      const invoices = await this.getInvoices(searchParams.data, searchParams.status, searchParams.props)

      // TODO: update with all_invoice_ids
      return invoices.rows.map((invoice) => {
        return invoice.id
      })
    }
    else {
      // Returning 1 === faster
      return (await Invoice.queryList(searchParams, 1)).data.all_invoice_ids
    }
  }

  async getOriginalsRequiredInvoices (id, page) {
    if (LaunchDarkly.isFlagEnabled(LDFlags.useV2InvoiceListing)) {
      return await searchV2Invoices('approved', {
        inputs: {
          clientID: id,
          flag: 'originals required',
        },
      },
      {
        page,
      })
    }
    else {
      return (await Invoice.queryList({
        client_id: id,
        limit: 25,
        offset: (page - 1) * 25,
        originals_required: true,
        status: 'approved',
      })).data
    }
  }

  async getPaperworkIssueInvoices (id, page) {
    if (LaunchDarkly.isFlagEnabled(LDFlags.useV2InvoiceListing)) {
      return await searchV2Invoices('approved', {
        inputs: {
          clientID: id,
          flag: 'unreadable,missing documents',
        },
      },
      {
        page,
      })
    }
    else {
      return (await Invoice.queryList({
        client_id: id,
        limit: 25,
        offset: (page - 1) * 25,
        paperwork_issues: true,
        status: 'approved',
      })).data
    }
  }

  async regenerateDocs (invoice) {
    return new InvoiceApiDelegator().regenerateDocs(invoice.client_id, {
      id: invoice.id,
    })
  }

  /**
   * @param {V1CreateInvoiceRequest} v1Request - V1 create invoice request
   * @returns {Promise<UiInvoicePossibleDuplicate[]>}
   */
  async getPossibleDuplicates (v1Request) {
    return await new InvoiceApiDelegator().getPossibleDuplicates(v1Request)
  }


  async getReserves (clientId, reason = null, page = 1, limit = 25) {
    let items
    let count

    if (LaunchDarkly.isFlagEnabled(LDFlags.useV2InvoiceListing)) {
      const response = await searchV2Reserves(clientId, reason, page, limit)
      items = response.items
      count = response.pagination.totalItems
    }
    else {
      const response = (await Reserve.getClientReserves({
        client_id: clientId,
        include: 'clientpayments',
        limit: limit,
        offset: page > 1
          ? (page - 1) * limit
          : 0,
        reason: reason === 'adjustment'
          ? null
          : reason,
        sort_by: 'accounting',
        sort_direction: 'DESC',
        type: reason === 'adjustment'
          ? 'client adjustments'
          : null,
      })).data

      items = response.rows
      count = response.count
    }

    return {
      items,
      count,
    }
  }

  async getReserveAccount (clientId, page = 1, limit = 10) {
    let items = []
    let count = 0

    if (LaunchDarkly.isFlagEnabled(LDFlags.useV2InvoiceListing)) {
      const response = await searchV2ReserveAccount(clientId, page, limit)
      items = response.items
      count = response.pagination.totalItems
    }
    else {
      const response = (await ReserveAccount.getClientReserves({
        client_id: clientId,
        limit: limit,
        offset: page > 1
          ? (page - 1) * limit
          : 0,
        sort_by: 'created_at',
        sort_direction: 'DESC',
      })).data

      items = response.rows.map((reserve) => {
        return {
          ...reserve,
          createdAt: reserve.created_at,
        }
      })
      count = response.count
    }

    return {
      items,
      count,
    }
  }

  async getUnknownDebtorID () {
    if (LaunchDarkly.isFlagEnabled(LDFlags.useV2InvoiceListing)) {
      return _Store.debtorNotFound.id
    }
    else {
      return await Debtor.getUnknownDebtorId()
    }
  }

  async getTransformedInvoice (id) {
    if (LaunchDarkly.isFlagEnabled(LDFlags.useV2InvoiceListing)) {
      return transformV2RawInvoice((await new InvoiceV2().get(id)).data)
    }
    else {
      return transformRawInvoice((await Invoice.get(id)).data)
    }
  }

  async postClientWire (params) {
    const v1Request = {
      ...params,
    }

    if (await LaunchDarkly.isFlagEnabledForClient(params.id, LDFlags.useTAPI)) {
      v1Request.useTransfersApi = true
    }

    return new TransfersApiDelegator().initiateExpediteTransfer(params.id, v1Request)
  }

  async initiateDebitTransfer (clientId, params) {
    const v1Request = {
      ...params,
    }

    return new TransfersApiDelegator().initiateDebitRegularTransfer(clientId, v1Request)
  }

  async postDebtorPayment (payload, clientId) {
    /*** @type {V1CreateBrokerPaymentRequest} */
    const v1Request = {
      amount: payload.amount
        ? convertBigToPennies(payload.amount.big)
        : "0",
      batch_date: moment(payload.batch_date).tz('America/New_York').toDate(),
      check_number: !payload.dataReason
        ? payload?.paymentMethod === 'ach'
          ? null
          :  validator.escape(payload.check_number)
        : undefined,
      invoice_id: payload.id,
      // undefined for nonpayments
      transaction_type: payload.paymentMethod
        ? payload.paymentMethod === 'ach'
          ? 'ach'
          : 'lockbox'
        : undefined,
      update_status: payload?.dataReason || null,
      update_type: 'approved',
    }
    return new BrokerPaymentApiDelegator().postBrokerPayment(clientId, v1Request)
  }

  /**
   * @param {V1CreateInvoiceRequest} v1Request - V1 create invoice request
   * @returns {Promise<UiInvoiceModel>}
   */
  async postInvoice (v1Request) {
    return await new InvoiceApiDelegator().postInvoice(v1Request)
  }

  async postInvoiceUpdates (componentData, type) {
    let payload

    // I'd rather the payload logic be here verse in components that don't care about what
    // the API requires other than "data", but the separate types of payloads is a result of
    // having different data structures in the components (i.e. data properties)
    // TODO: refactor said components to have the same data() structure
    if (type === 'update') {
      if ([ 'payment', 'paid to client' ].includes(componentData.update_status)) {
        let checkNumberOrACH

        if (componentData.inputs.paymentMethod === 'ach') {
          checkNumberOrACH = 'ach'
        }
        else if (componentData.inputs.check.value) {
          checkNumberOrACH = XSSProtection(componentData.inputs.check.value)
        }
        else {
          checkNumberOrACH = null
        }

        payload = {
          check_number_or_ach: checkNumberOrACH,
          invoice_id: componentData.$props.invoice.id,
          // TODO: fix this ass fuckery backend
          // https://app.zenhub.com/workspaces/bobtail-5c64d9b0b66dba3bb32bda6e/issues/fs-bobtail/bobtail/1921
          'issue date': componentData.inputs.issue_date
            ? componentData.inputs.issue_date
            : null,
          notes: componentData.inputs.notes
            // Notes is typed by the user - blacklist code characters
            ? XSSProtection(componentData.inputs.notes)
            : null,
          talked_to: componentData.inputs.talked_to
            // Talked To is typed by the user - blacklist code characters
            ? XSSProtection(componentData.inputs.talked_to)
            : null,
          talked_to_contact_method: componentData.inputs.talked_to_contact_method,
          update_status: componentData.update_status,
          update_type: componentData.status,
        }
      }
      else {
        payload = {
          invoice_id: componentData.$props.invoice.id,
          // TODO: fix this ass fuckery backend
          // https://app.zenhub.com/workspaces/bobtail-5c64d9b0b66dba3bb32bda6e/issues/fs-bobtail/bobtail/1921
          'issue date': componentData.inputs.issue_date,
          metadata: {
            broker_contact: componentData.inputs.broker_contact
              ? XSSProtection(componentData.inputs.broker_contact)
              : null,
            check_number: componentData.inputs.check.value
              ? XSSProtection(componentData.inputs.check.value)
              : null,
            date: componentData.inputs.date
              ? moment(componentData.inputs.date).tz('America/New_York').format('MM/DD/YY')
              : null,
            load_number: componentData.inputs.load_number
              ? XSSProtection(componentData.inputs.load_number)
              : null,
            phone_number: componentData.inputs.phone_number
              ? XSSProtection(componentData.inputs.phone_number)
              : null,
            previous_factoring_company: componentData.inputs.previous_factoring_company
              ? XSSProtection(componentData.inputs.previous_factoring_company)
              : null,
          },
          missing: componentData.inputs.missing
            ? XSSProtection(componentData.inputs.missing)
            : null,
          notes: componentData.inputs.notes
            ? XSSProtection(componentData.inputs.notes)
            : null,
          send_firebase_notification: AllFlags.includes(componentData.update_status),
          talked_to: componentData.inputs.talked_to
            ? XSSProtection(componentData.inputs.talked_to)
            : null,
          talked_to_contact_method: componentData.inputs.talked_to_contact_method,
          update_status: componentData.update_status,
          update_type: componentData.update_status !== 'note'
            ? componentData.status
            : 'note',
        }
      }
    }
    else if (type === 'flag') {
      payload = {
        invoice_id: componentData.invoice.id,
        notes: componentData.dataNote,
        send_firebase_notification: true,
        update_status: componentData.dataReason,
        update_type: 'pending',
      }
    }
    else if (type === 'waitingForResponse') {
      payload = componentData
    }
    else if (type === 'notify') {
      payload = {
        invoice_id: componentData.invoice.id,
        notes: componentData.updateMessage,
        update_status: 'flag notification sent',
        update_type: 'note',
      }
    }

    const clientId = type === 'update'
      ? componentData.$props.invoice.client.id
      : componentData.invoice.client.id
    return new InvoiceApiDelegator().postInvoiceUpdate(clientId, payload)
  }

  async postReserve (v1RequestBody) {
    if (await this.#isIngestThroughV2EnabledForClient(v1RequestBody.client_id)) {
      const v2RequestBody = ReservesMapper.v1ToCreateRequest(v1RequestBody, undefined, true)
      const v2ReserveClient = new ReserveV2(v1RequestBody.client_id)
      const v2Response = await v2ReserveClient.create(v2RequestBody)

      // TODO: remove when BE updates reserve to contain the total
      v2Response.total = (await v2ReserveClient.total()).amount
      return ReservesMapper.v2ReserveToV1(v2Response)
    }
    else {
      const v1Response = (await Reserve.create(v1RequestBody)).data
      await this.#v2FnWrapper(async () => {
        const v2RequestBody = ReservesMapper.v1ToCreateRequest(v1RequestBody, v1Response)
        const v2ReserveClient = new ReserveV2(v1RequestBody.client_id)
        const v2Response = await v2ReserveClient.create(v2RequestBody)

        if (LaunchDarkly.isFlagEnabled(LDFlags.useV2InvoiceListing)) {
          // TODO: remove when BE updates reserve to contain the total
          v2Response.total = (await v2ReserveClient.total()).amount

          return ReservesMapper.v2ReserveToV1(v2Response)
        }
      },
      v1RequestBody.client_id,
      {
        operationName: 'post-reserve',
      }
      )
      return v1Response
    }
  }

  /**
   * @param {V1CreateReserveAccountRequest} reserveRequestBody - V1 request body
   * @param clientId
   * @param amount
   * @param notes
   * @returns {Promise<any>}
   */
  async postReserveAccount (clientId, amount, notes) {
    const v1Request = {
      amount,
      notes,
      client_id: clientId,
    }

    return await new ReserveAccountApiDelegator().postReserveAccount(clientId, v1Request)
  }

  /**
   * @param {string} id - ID of the reserve
   * @param {string} clientId - ID of the client
   * @returns {Promise<V1ReserveResponse>}
   */
  async deleteReserve (id, clientId) {
    return await new ReserveApiDelegator().delete(clientId, id)
  }

  async purchaseInvoice (invoice, adjustment = null) {
    const deduction = adjustment
      ? convertBigToPennies(adjustment.big)
      : '0'
    /** @type {V1PurchaseInvoiceRequest} */
    const v1Request = {
      approved_invoice_adjustment_amount: deduction,
      id: invoice.id,
      send_firebase_notification: true,
      status: 'approved',
    }
    return await new InvoiceApiDelegator().purchaseInvoice(invoice.client_id, v1Request)
  }

  async putDebtorPayment (payload, clientId) {
    /*** @type{V1UpdateBrokerPaymentRequest} */
    const v1Request = {
      batch_date: payload.batch_date,
      check_number: payload.transaction_type === 'lockbox'
        ? payload.check_number
        : null,
      id: payload.id,
      // keep the original_id in debtor_payments_history when make an update on debtor payment
      keepOriginalID: true,
      transaction_type: payload.transaction_type,
    }
    return new BrokerPaymentApiDelegator().updateBrokerPayment(clientId, v1Request)
  }

  async rejectInvoice (invoice, note, reason, declinedReason) {
    /*** @type {V1RejectInvoiceRequest} */
    const v1Request = {
      id: invoice.id,
      notes: note,
      send_firebase_notification: true,
      status: 'declined',
      update_status: reason,
      declined_reason: declinedReason.replace('.', '').charAt(0).toUpperCase() + declinedReason.slice(1),
    }
    return await new InvoiceApiDelegator().rejectInvoice(invoice.client_id, v1Request)
  }

  async revertInvoice (id, returnToStatus = 'pending', clientId) {
    /*** @type {V1RevertInvoiceRequest} */
    const v1Request = {
      approved_date: null,
      id,
      paid_date: null,
      status: returnToStatus,
    }
    return new InvoiceApiDelegator().revertInvoice(clientId, v1Request)
  }

  /**
   * @param {string} clientId - ID of the client
   * @param {V1UpdateClientRequest} payload - V1 update client request body
   * @returns {Promise<any>}
   */
  async updateClientProfile (clientId, payload) {
    return new ClientApiDelegator().updateClientProfile(clientId, payload)
  }

  /**
   * @param {string} debtorId - ID of the debtor
   * @param {V1UpdateDebtorRequest} payload - v1 payload
   * @returns {Promise<any>}
   */
  async updateDebtorProfile (debtorId, payload) {
    const v2Payload = DebtorMapper.v1UpdateBrokerRequestToV2(payload)
    await new BrokerV2().updateConfiguration(debtorId, v2Payload)
    return (await Debtor.get(debtorId)).data
  }

  /**
   * @param {string} clientId - ID of the client
   * @param {V1UpdateClientRequest} payload - V1 update client request body
   * @returns {Promise<any>}
   */
  async updateClientStatus (clientId, payload) {
    return new ClientApiDelegator().updateClientStatus(clientId, payload)
  }

  /**
   * @param {V1UpdateInvoiceRequest} payload - V1 update invoice request body
   * @returns {Promise<UiInvoiceModel>}
   */
  async updateInvoice (payload) {
    return new InvoiceApiDelegator().updateInvoice(payload)
  }

  /**
   * @typedef {object} WaitForVerificationPayload
   * @property {string} invoice_id - ID of the invoice
   * @property {string=} notes - The notes for marking an invoice as verified
   * @property {string=} talked_to - The person contacted to set the appropiate verification status
   * @property {string=} talked_to_contact_method - The type of communication used to contact the person
   */

  /**
   * @param {string} clientId - ID of the client
   * @param {WaitForVerificationPayload} payload - payload
   * @returns {Promise<any>}
   */
  async waitForVerificationResponse (clientId, payload) {
    /*** @type {V1WaitOnVerificationInvoiceRequest} */
    const v1Request = {
      invoice_id: payload.invoice_id,
      talked_to: payload.talked_to,
      talked_to_contact_method: payload.talked_to_contact_method,
      notes: payload.notes,
      update_status: 'waiting on verification',
      update_type: 'pending',
    }
    return await new InvoiceApiDelegator().waitForVerification(clientId, v1Request)
  }

  /**
   * @param {V1VerifyInvoiceRequest} v1Request - V1 verify invoice request body
   * @returns {Promise<UiInvoiceModel>}
   */
  async verifyInvoice (v1Request) {
    return new InvoiceApiDelegator().verifyInvoice(v1Request)
  }

  /**
   * @param {string} clientId - ID of the client
   * @returns {Promise<UiReserveRateHistoryResponse[]>}
   */
  async getReserveRateHistory (clientId) {
    const items = await new ClientApiDelegator().getReserveRateHistory(clientId)
    return items.map((item) => {
      const user = item.updated_by_user.email === 'system@bobtail.com'
        ? 'System'
        : `${item.updated_by_user.employee?.first_name} ${item.updated_by_user.employee?.last_name}`

      return {
        created_at: moment(item.updated_at).format('M/D/YY'),
        notes: item.changelog_notes || '',
        rate: item.reserve_rate_percentage,
        reason: item.reserve_rate_change_reason,
        updated_by: user,
      }
    })
  }

  /**
   * @param {string} clientId - ID of the client
   * @returns {Promise<UiClientLimitHistoryResponse[]>}
   */
  async getClientLimitHistory (clientId) {
    const items = await new ClientApiDelegator().getClientLimitHistory(clientId)
    return items.map((item) => {
      const user = item.updated_by_user.email === 'system@bobtail.com'
        ? 'System'
        : `${item.updated_by_user.employee?.first_name} ${item.updated_by_user.employee?.last_name}`

      return {
        created_at: moment(item.updated_at).format('M/D/YY'),
        notes: item.changelog_notes || '',
        client_limit: item.client_limit,
        updated_by: user,
      }
    })
  }

  /**
   * @param {string} debtorId - ID of the client
   * @returns {Promise<UiDebtorLimitHistoryResponse[]>}
   */
  async getDebtorLimitHistory (debtorId) {
    const items = (await Debtor.history({
      fields: 'debtor_limit',
      id: debtorId,
    })).data?.rows || []

    return items.map((item) => {
      const user = item.updated_by_user.email === 'system@bobtail.com'
        ? 'System'
        : `${item.updated_by_user.employee?.first_name} ${item.updated_by_user.employee?.last_name}`

      return {
        created_at: moment(item.updated_at).format('M/D/YY'),
        notes: item.changelog_notes || '',
        debtor_limit: item.debtor_limit,
        updated_by: user,
      }
    })
  }

  /**
   * @param {string} clientId - ID of the client
   * @returns {Promise<string>}
   */
  async getClientReserveTotal (clientId) {
    return (await new ClientApiDelegator().getReserveTotal(clientId)).total
  }

  /**
   * @param {*} v1Request - v1 Request
   * @returns {Promise<V2MaintenanceMode>}
   */
  async updateMaintenanceMode (v1Request) {
    const v2Request = MaintenanceModeMapper.v1UpdateRequestToV2UpdateRequest(v1Request)
    return (await new MaintenanceV2().update(v2Request))
  }

  /**
   * @returns {Promise<V2MaintenanceMode>}
   */
  async getMaintenanceMode () {
    const v1Response = (await Tenant.fetchMaintenanceMode()).data
    return MaintenanceModeMapper.v1MaintenanceToV2Maintenance(v1Response)
  }

  /**
   * @param {V1CreateProcessingNotesRequest} v1Payload - v1 request payload
   * @returns {Promise<any>}
   */
  async createProcessingNotes (v1Payload) {
    const v2Request = ProcessingNotesMapper.v1CreateRequestToV2CreateRequest(v1Payload)
    return (await new ProcessingNotesV2().create(v2Request))
  }

  /**
   * @param {string} id - id of the processing note
   * @param {V1UpdateProcessingNotesRequest} v1Payload - v1 request payload
   * @returns {Promise<any>}
   */
  async updateProcessingNotes (id, v1Payload) {
    const v2Request = ProcessingNotesMapper.v1UpdateRequestToV2UpdateRequest(v1Payload)
    return (await new ProcessingNotesV2().update(id, v2Request))
  }

  /**
   * @param {string} noteId - note id
   * @returns {Promise<any>}
   */
  async deleteProcessingNotes (noteId) {
    return (await new ProcessingNotesV2().delete(noteId, { __ingestThrough: true, __v1Payload: {} }))
  }

}

export default FactoringAPI
